import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PersonForm from "../../common/userRegistration/PersonForm";

export default function AddEntityModal({
  onClose,
  onSubmit,
  initialData = {},
  lockedCountryName = "",
  defaultCountryName = "",
  submitLabel = "Save",
  maxWidth = 720,
  closeOnBackdrop = true,
  busy = false,
}) {
  const contentRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKeyDown);
    setTimeout(() => contentRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const initialPersonData = {
    name: initialData.name || "",
    lastName: initialData.lastName || "",
    phone: initialData.phone || "",
    country: initialData.country || lockedCountryName || defaultCountryName || "",
    dialCode: initialData.dialCode || "",
    nationalNumber: initialData.nationalNumber || "",
    phoneValid: initialData.phoneValid ?? false,
  };

  const isCountryLocked = !!lockedCountryName?.trim();

  const handlePersonSubmit = (payload) => {
    onSubmit?.(payload);
  };

  return createPortal(
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(17,24,39,.45)", zIndex: 1080 }}
      onClick={() => closeOnBackdrop && onClose?.()}
      aria-modal="true"
      role="dialog"
      aria-labelledby="add-entity-modal-title"
    >
      <div
        className="d-flex w-100 h-100 justify-content-center align-items-center p-3 p-sm-4"
        onClick={(e) => e.stopPropagation()}
      >
        <section
          className="ui-panel w-100"
          style={{ maxWidth }}
          ref={contentRef}
          tabIndex="-1"
        >
          <header className="ui-panel__header">
            <div className="d-flex align-items-center justify-content-between">
              <h3 id="add-entity-modal-title" className="ui-title mb-0">Customer Info</h3>
              <div className="ui-actions m-0">
                <button
                  type="button"
                  className="ui-btn"
                  onClick={onClose}
                  disabled={busy}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="ui-subtitle mt-1">Fill in the customer information below.</div>
          </header>

          <PersonForm
            title="Customer Info"
            initialData={initialPersonData}
            lockOnCountryName={isCountryLocked ? lockedCountryName : null}
            step={1}
            onSubmit={handlePersonSubmit}
            showPanel={false}
            showHeader={false}
            showStepBadge={false}
            formClassName="p-3 p-sm-4"
            actionsRenderer={() => (
              <div className="ui-actions w-100 justify-content-end gap-2 pt-3">
                <button
                  type="button"
                  className="ui-btn"
                  onClick={onClose}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary rounded-pill"
                  disabled={busy}
                >
                  {submitLabel}
                </button>
              </div>
            )}
          />
        </section>
      </div>
    </div>,
    document.body
  );
}

AddEntityModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  lockedCountryName: PropTypes.string,
  defaultCountryName: PropTypes.string,
  submitLabel: PropTypes.string,
  maxWidth: PropTypes.number,
  closeOnBackdrop: PropTypes.bool,
  busy: PropTypes.bool,
};
